import { createStart, createCsrfMiddleware, createMiddleware } from "@tanstack/react-start";

import { attachSupabaseAuth } from "./integrations/supabase/auth-attacher";
import { renderErrorPage } from "./lib/error-page";
import { toxicologyRedirect } from "./lib/regulatoryToxicology";
import { retiredQaOfferingRedirect } from "./lib/retiredQaOffering";


const errorMiddleware = createMiddleware().server(async ({ next }) => {
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    console.error(error);
    return new Response(renderErrorPage(), {
      status: 500,
      headers: { "content-type": "text/html; charset=utf-8" },
    });
  }
});

// Prefix redirects cover all old hubs/subpages, including trailing slashes and queries.
const serviceRenameMiddleware = createMiddleware().server(async ({ next, request }) => {
  return retiredQaOfferingRedirect(request.url) ?? toxicologyRedirect(request.url) ?? next();
});

// Retrieval instrumentation: record document requests coming from AI assistant
// crawlers (GPTBot, ClaudeBot, PerplexityBot, ...) so the admin dashboard can
// show which pages the answer engines actually fetch. The logger lives in a
// *.server module and is imported dynamically so it never reaches the browser
// bundle, and the insert is fire-and-forget.
const aiCrawlerMiddleware = createMiddleware().server(async ({ next, request }) => {
  try {
    const userAgent = request.headers.get("user-agent") ?? "";
    if (/bot|crawl|spider|gpt|claude|perplexity|anthropic|ai-agent/i.test(userAgent)) {
      const { logAiCrawlerHit } = await import("./lib/aiCrawlerLog.server");
      const pathname = new URL(request.url).pathname;
      // Routes that log themselves with a more specific kind, plus internal
      // endpoints, are skipped so a single fetch never produces two rows.
      const selfLogged =
        pathname.startsWith("/md/") ||
        pathname.startsWith("/_serverFn") ||
        pathname.startsWith("/api/") ||
        pathname === "/llms.txt" ||
        pathname === "/llms-full.txt" ||
        pathname === "/sitemap.xml" ||
        pathname === "/rss.xml";
      if (!selfLogged) logAiCrawlerHit(request, "page");
    }
  } catch {
    // Never let instrumentation break a request.
  }
  return next();
});

// Start installs this automatically when src/start.ts is absent; defining the
// file opts out, so re-add it explicitly to keep server functions protected
// from cross-site requests.
const csrfMiddleware = createCsrfMiddleware({
  filter: (ctx) => ctx.handlerType === "serverFn",
});

export const startInstance = createStart(() => ({
  requestMiddleware: [errorMiddleware, serviceRenameMiddleware, csrfMiddleware, aiCrawlerMiddleware],
  functionMiddleware: [attachSupabaseAuth],
}));


